import { ColorType, PatternObject, SeriesLineOptions } from 'highcharts';
import {
    ChartPoint,
    ChartSeries,
    ChartTooltipFormatterContextObject,
    HighChartsSerieDatetimeValue,
} from 'adapter/in/common/components/widgets/Chart/types';
import { DateFormatter } from 'adapter/in/common/components/widgets/utils/dateFormatter';
import { applyTwoDecimals } from 'adapter/in/common/utils/numberFormatter';
import i18n from 'asset/translation';

export const findComparisonDateIfAvailable = (series: ChartSeries[], serieName: string, x: number) =>
    findComparisonSerie(series, serieName)?.data?.find((data) => data.x === x)?.unmodifiedX;

export const findComparisonSerie = (series: ChartSeries[], serieName: string) =>
    series.find(
        (serie) =>
            serie.name.startsWith(serieName) && serie.name.split('(').length > serieName.split('(').length && serie.userOptions.comparison
    );

export const findNotComparisonSerie = (series: ChartSeries[], serieName: string) =>
    series.find(
        (serie) =>
            serieName.startsWith(serie.name) && serie.name.split('(').length < serieName.split('(').length && !serie.userOptions.comparison
    );

export const tooltipFormatter = (
    points: ChartTooltipFormatterContextObject,
    sharedTooltip: boolean,
    isScatterTooltip: boolean,
    dateFormat: string,
    hasTitle: boolean,
    areXAxesCategorized: boolean,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    if (sharedTooltip) {
        return sharedTooltipFormatter(points, dateFormat, thousandSeparator, decimalSeparator);
    }
    if (areXAxesCategorized) {
        return categorizedTooltipFormatter(points, thousandSeparator, decimalSeparator);
    }
    if (isScatterTooltip) {
        return scatterTooltipFormatter(points, dateFormat, thousandSeparator, decimalSeparator);
    }
    return splittedTooltipFormatter(points, dateFormat, hasTitle, thousandSeparator, decimalSeparator);
};

const categorizedTooltipFormatter = (points: ChartTooltipFormatterContextObject, thousandSeparator: string, decimalSeparator: string) =>
    `<span style="color:${getColor(points.color)}">\u25CF </span>${points.series.name}` +
    `: ` +
    `<span style="font-weight:bold">${applyTwoDecimals((points.y as number).toString(), decimalSeparator, thousandSeparator)} ${
        (points.series.options as SeriesLineOptions).tooltip?.valueSuffix
    }</span><br/>`;

const findSharedComparisonDateIfAvailable = (points: ChartTooltipFormatterContextObject[]) => {
    const foundUnmodifiedX = points.find((value) => ((value.point as unknown) as HighChartsSerieDatetimeValue).unmodifiedX !== undefined);
    return foundUnmodifiedX ? ((foundUnmodifiedX.point as unknown) as HighChartsSerieDatetimeValue).unmodifiedX : undefined;
};

export const sharedTooltipFormatter = (
    points: ChartTooltipFormatterContextObject,
    dateFormat: string,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const comparisonDate = findSharedComparisonDateIfAvailable(points.points);
    const titleBeginning = `${new DateFormatter(dateFormat).dh(new Date(points.points[0].x as number))}`;
    const titleEnding = comparisonDate
        ? `${i18n.t('common.comparedTo').toLowerCase()} ${new DateFormatter(dateFormat).dh(comparisonDate)} <br/>`
        : '<br/>';

    return points.points.reduce(
        (s: string, point: ChartTooltipFormatterContextObject) =>
            `${s}<span style="color:${point.color}">\u25CF </span>${point.series.name}` +
            `: ` +
            `<span style="font-weight:bold">${
                point.y !== null && point.y !== undefined
                    ? applyTwoDecimals(point.y.toString(), decimalSeparator, thousandSeparator)
                    : 'N/A'
            } ${(point.series.options as SeriesLineOptions).tooltip?.valueSuffix}</span><br/>`,

        `${titleBeginning} ${titleEnding}`
    );
};

export const scatterTooltipFormatter = (
    points: ChartTooltipFormatterContextObject,
    dateFormat: string,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const serie = points.series;
    const x = points.x as number;
    const serieData = serie.data.find((data) => data.x === x);
    const unitsX = serie.xAxis.userOptions.labels?.format?.replace('{value} ', '') ?? 'N/A';
    const unitsY = serie.yAxis.userOptions.labels?.format?.replace('{value} ', '') ?? 'N/A';
    const color = points.color;
    const name = serie.name;
    const valueX = `${
        serieData?.x !== undefined && serieData.x !== null
            ? applyTwoDecimals(serieData.x.toString(), decimalSeparator, thousandSeparator)
            : 'N/A'
    } ${unitsX}`;
    const valueY = `${
        serieData?.y !== undefined && serieData.y !== null
            ? applyTwoDecimals(serieData.y.toString(), decimalSeparator, thousandSeparator)
            : 'N/A'
    } ${unitsY}`;
    return `<span><span style="color:${color}">\u25CF </span><span style="font-weight:bold;">${valueX} | ${valueY}</span> ${new DateFormatter(
        dateFormat
    ).dh(serieData!.t!)}</span><br>${name}</span>`;
};

const buildTooltipSerie = (
    serie: {
        isHighlighted: boolean;
        color: string;
        name: string;
        value: string;
        valueStyle: string;
    },
    comparisonSerie?: { value: string; valueStyle: string }
) =>
    `<span style="opacity: ${serie.isHighlighted ? 1 : 0.5}"><span style="color:${serie.color}">\u25CF </span>${
        serie.name
    }: <span style="font-weight:bold;${serie.valueStyle}">${serie.value}</span>${
        comparisonSerie ? ` | <span style="font-weight:bold;${comparisonSerie.valueStyle}">${comparisonSerie.value}</span>` : ''
    }</span><br/>`;

export const splittedTooltipFormatter = (
    points: ChartTooltipFormatterContextObject,
    dateFormat: string,
    hasTitle: boolean,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const x = points.x as number;
    const serieName = points.series.name;
    let comparisonDate = points.point.unmodifiedX;
    const series = points.series.chart.series;
    let title = '';
    if (hasTitle) {
        if (!comparisonDate) {
            comparisonDate = findComparisonDateIfAvailable(series, serieName, x);
        }
        title += `${new DateFormatter(dateFormat).dh(new Date(points.point.x))}`;
        if (comparisonDate) {
            title = `${title} ${i18n.t('common.comparedTo').toLowerCase()} ${new DateFormatter(dateFormat).dh(comparisonDate)}`;
        }
        title += ' <br/>';
    }

    const defaultValue = 'N/A';

    const validColor = (color: unknown) => typeof color === 'string';
    return series
        .filter((serie) => serie.visible && serie.userOptions.enableMouseTracking)
        .reduce((s: string, serie: ChartSeries) => {
            const noComparison = findNotComparisonSerie(series, serie.name);
            const isNoComparison = noComparison && noComparison.visible === false;
            if (!serie.userOptions.comparison || (serie.userOptions.comparison && !noComparison) || isNoComparison) {
                const isSelectedSerieComparison =
                    serieName.startsWith(serie.name) && serieName.split('(').length > serie.name.split('(').length;
                const comparisonSerie = noComparison ?? findComparisonSerie(series, serie.name);
                const serieData = serie.data.find((data) => data?.x === x);
                const isHighlighted = (serieData && points.x === serieData?.x && points.y === serieData?.y) || isSelectedSerieComparison;

                serie.points.forEach((_point) => {
                    if (_point?.setState) _point.setState('');
                });

                comparisonSerie?.points.forEach((_point) => {
                    if (_point?.setState) _point.setState('');
                });

                if (isHighlighted) {
                    if (serie.visible) {
                        if (serie.setState) serie.setState('hover');

                        const currentPoint = serie.points.find((_point) => _point.y !== null && _point.x === serieData?.x);
                        if (currentPoint && currentPoint.setState) {
                            currentPoint.setState('hover');
                        }
                    }

                    if (comparisonSerie?.visible) {
                        if (comparisonSerie?.setState) {
                            comparisonSerie.setState('hover');
                        }

                        const currentComparisonPoint = comparisonSerie?.points.find(
                            (_point) => _point.y !== null && _point.x === serieData?.x
                        );
                        if (currentComparisonPoint?.setState) {
                            currentComparisonPoint.setState('hover');
                        }
                    }
                }

                const units = (serie.options as SeriesLineOptions).tooltip?.valueSuffix ?? defaultValue;
                const value = `${getValue(serie, serieData, x, decimalSeparator, thousandSeparator, defaultValue)} ${units}`;
                const valueStyle = !isSelectedSerieComparison && isHighlighted ? ' text-decoration: underline;' : '';
                const color = (serieData?.color as string) ?? (serie.color as string);
                const comparisonSerieData = comparisonSerie?.data.find((data) => data.x === x);
                const comparisonColor = comparisonSerieData?.color && getColor(comparisonSerieData.color);
                const name = noComparison ? noComparison.name : serie.name;
                const comparisonValue = `${getValue(
                    serie,
                    comparisonSerieData,
                    x,
                    decimalSeparator,
                    thousandSeparator,
                    defaultValue
                )} ${units}`;
                const comparisonValueStyle = isSelectedSerieComparison && isHighlighted ? ' text-decoration: underline;' : '';
                const tooltipSerie = buildTooltipSerie(
                    {
                        isHighlighted,
                        color: !isSelectedSerieComparison && validColor(color) ? color : comparisonColor ?? '#000000',
                        name,
                        value: !isNoComparison ? value : comparisonValue,
                        valueStyle: !isNoComparison ? valueStyle : comparisonValueStyle,
                    },
                    comparisonSerie
                        ? {
                              value: isNoComparison ? value : comparisonValue,
                              valueStyle: isNoComparison ? valueStyle : comparisonValueStyle,
                          }
                        : undefined
                );
                return `${s}${tooltipSerie}`;
            }
            return s;
        }, title);
};

const getValue = (
    serie: ChartSeries,
    serieData: ChartPoint | undefined,
    x: number,
    decimalSeparator: string,
    thousandSeparator: string,
    defaultValue: string
) => {
    let foundSerieData = serieData;
    let previousValue = false;
    if (!foundSerieData) {
        previousValue = true;
        foundSerieData = serie.data.findLast((data) => data.x <= x);
    }

    if (foundSerieData && foundSerieData.y !== undefined && foundSerieData.y !== null) {
        return `${applyTwoDecimals(foundSerieData.y.toString(), decimalSeparator, thousandSeparator)}${previousValue ? '*' : ''}`;
    }
    return defaultValue;
};

const getColor = (color?: ColorType) =>
    (color as PatternObject).pattern !== undefined ? (color as PatternObject).pattern.color : (color as string);
